import api from '../../api/api'

export type Access = { id: string; role: 'kiosk' | 'pass' | 'challenge'; credential: string }
export type DemoSession = {
  id: string
  kiosk_key: string
  pass_key: string
  participant_name: string
  expires_at: string
}
export type CheckInEvent = { id: string; action: 'in' | 'out'; occurred_at: string; source: string }
export type CheckInState = {
  id: string
  name: string
  participant_name: string
  inside: boolean
  server_time: string
  arrived_at: string | null
  completed_seconds: number
  events: CheckInEvent[]
  challenge: string | null
  expires_at: string
  company_logo: string | null
  employee_photo?: string | null
  employee_title?: string
  wallet: { apple: boolean; google: boolean; apple_nfc?: boolean; google_nfc?: boolean }
}
export const checkInCapabilities = async () =>
  (await api.get<{ enabled: boolean }>('/office-checkin/capabilities/')).data
export const createDemo = async () => (await api.post<DemoSession>('/office-checkin/sessions/')).data
export const endDemo = async (id: string) => api.post(`/office-checkin/sessions/${id}/revoke/`)
export function checkInLink(access: Access) {
  return `${window.location.origin}/office-checkin/${access.role}#${new URLSearchParams({ id: access.id, key: access.credential })}`
}
export function readAccess(): Access | null {
  const params = new URLSearchParams(window.location.hash.slice(1))
  const role = window.location.pathname.split('/').pop()
  const id = params.get('id'),
    credential = params.get('key')
  if (!id || !credential || !/^[\da-f-]{36}$/i.test(id) || !['kiosk', 'pass', 'challenge'].includes(role || ''))
    return null
  return { id, credential, role: role as Access['role'] }
}
export async function publicRequest<T>(access: Access, endpoint: string, extra = {}): Promise<T> {
  // No Firebase token, cookies or telemetry on the public device-pairing surface.
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT.replace(/\/$/, '')}/office-checkin/sessions/${access.id}/${endpoint}/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      cache: 'no-store',
      signal: AbortSignal.timeout(12000),
      body: JSON.stringify({ role: access.role, credential: access.credential, ...extra }),
    },
  ).catch(() => {
    throw new Error('We cannot reach AboutWork right now. Check your connection and try again.')
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(
      typeof data.detail === 'string' ? data.detail : 'Could not connect. Check your connection and try again.',
    )
  }
  if (response.headers.get('content-type')?.includes('application/vnd.apple.pkpass'))
    return (await response.blob()) as T
  return response.json()
}
